import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import styled from 'styled-components'
import {
  ContentWidth,
  TextWidth,
  TextWidthExtended,
} from '@ta-interaktiv/react-disco-grid'
import { GlobalVariables } from 'Globals/GlobalVariables'
import { useEffect, useState } from 'react'
import { useWindowSize } from 'usehooks-ts'
import { AnnotatedImage } from 'components/AnnotatedImage'
import {
  Capital,
  Country,
  CountrySmall,
  LabelUnimportant,
  LabelUnimportantInverted,
  Region,
  RegionInverted,
  SatAnnotationGray,
  SatAnnotationText,
  SatAnnotationWhite,
  SatAnnotationYellow,
  SmallLabel,
} from 'components/MapLabels'

const LabelsDE = {
  saporischschja: 'Saporischschja',
  kiew: 'Kiew',
  ukraine: 'Ukraine',
  russia: 'Russland',
  trenches: 'Schützengräben',
  dragonsteeth: 'Panzersperren',
  cherson: 'Cherson',
  tokmak: 'Tokmak',
  donetsk: 'Donezk',
  bachmut: 'Bachmut',
  crimea: 'Krim',
  blacksea: 'Schwarzes Meer',
  charkiw: 'Charkiw',
  saporischa: 'Saporischschja',
  mykolaiv: 'Mikolajiw',
  odessa: 'Odessa',
  dnipro: 'Dnjepr',
  sumy: 'Sumi',
  asozsea: 'Asowsches Meer',
  mariupol: 'Mariupol',
  melitopol: 'Melitopol',
  berdschansk: 'Berdschansk',
  bilmak: 'Bilmak',
  akwsaporischschja: 'AKW Saporischschja',
  oleschki: 'Oleschki',
  dschankoj: 'Dschankoi',
  krasnoperekopsk: 'Krasnoperekopsk',
  novakachovka: 'Nowa Kachowka',
  armjansk: 'Armjansk',
  mychajliwka: 'Michailowka',
}
const debug = window.document.title === 'TA-Interaktiv Microfrontend'
const debugText = (
  <TextWidth>
    <p>
      Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy
      eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam
      voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet
      clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit
      amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam
      nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed
      diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum.
      Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor
      sit amet.
    </p>
  </TextWidth>
)
const colors = {
  bb3: 'rgba(0,0,0,0.3)',
  bb2: 'rgba(0,0,0,0.3)',
}

const MapAnnotation = {
  showMarker: false,
  lineStyle: {
    stroke: 'var(--yellow-main)',
  },
  makerStyle: {
    fill: 'var(--yellow-main)',
  },
}
const SmallMapAnnotations = [
  {
    content: <CountrySmall>{LabelsDE.ukraine}</CountrySmall>,
    position: { x: 40, y: 36 },
  },
  {
    content: <CountrySmall>{LabelsDE.russia}</CountrySmall>,
    position: { x: 70, y: 15 },
  },
]
const MainComponent = ({
  tenant,
  colorMode,
  properties,
}: MicroFrontendProps) => {
  const { width } = useWindowSize()
  const [isMobile, setIsMobile] = useState(false)

  const part = properties?.part || '1'

  useEffect(() => {
    setIsMobile(width < 950)
  }, [width])

  const sLH = width < 950 ? 24 : 27
  const isDarkMode = colorMode === 'dark'
  return (
    <AppContainer $colorMode={colorMode}>
      {(part === '1' || debug) && (
        <Center style={{ marginTop: 0 }}>
          <AnnotatedImage
            img={require('../../assets/maps/header_sat_' +
              (isMobile ? 'mob' : 'desk') +
              
              '.jpg')}
            source={'Quelle: New York Times, Planet Labs Inc.'}
            annotations={[
              {
                ...MapAnnotation,
                content: <SatAnnotationWhite>Wyskrywa</SatAnnotationWhite>,
                position: isMobile ? { x: 140, y: 35 } : { x: 64, y: 60 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationText>
                    <tspan>Schützengräben, Panzersperren und </tspan>
                    <tspan x={0} dy={sLH}>
                      andere militärische Hindernisse:
                    </tspan>
                    <tspan x={0} dy={sLH}>
                      Die russische Armee bereitet sich
                    </tspan>
                    <tspan x={0} dy={sLH}>
                      seit Monaten auf die bald beginnende
                    </tspan>
                    <tspan x={0} dy={sLH}>
                      Frühjahrsoffensive der Ukrainer vor.
                    </tspan>
                    <tspan x={0} dy={sLH}>
                      Die Verteidigungsstellungen (bei der Stadt
                    </tspan>
                    <tspan x={0} dy={sLH}>
                      Popasna in der Ostukraine) sind auf
                    </tspan>
                    <tspan x={0} dy={sLH}>
                      hochaufgelösten Satellitenbildern sichtbar.
                    </tspan>
                  </SatAnnotationText>
                ),
                position: isMobile
                  ? { x: 50, y: width < 500 ? 47 : 18 }
                  : { x: 50, y: width > 1300 ? 17 : 34 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow>
                    <tspan>Militärische</tspan>
                    <tspan x={0} dy={22}>
                      Ausrüstung
                    </tspan>
                  </SatAnnotationYellow>
                ),
                position: isMobile ? { x: 95, y: 60 } : { x: 46, y: 53 },
                textOffset: isMobile ? { x: -100, y: 0 } : { x: 130, y: 0 },
              },
              {
                // First
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow>Schützengräben</SatAnnotationYellow>
                ),
                position: isMobile ? { x: 34, y: 29 } : { x: 20, y: 25 },
                textOffset: isMobile ? { x: 90, y: 0 } : { x: 60, y: 60 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow>Schützengräben</SatAnnotationYellow>
                ),
                position: isMobile
                  ? { x: 40, y: width < 500 ? -30 : 48.5 }
                  : { x: 73, y: 65 },
                textOffset: isMobile ? { x: 0, y: 0 } : { x: 60, y: 0 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow>Schützengräben</SatAnnotationYellow>
                ),
                position: isMobile
                  ? { x: 140, y: width < 500 ? -30 : 48.5 }
                  : { x: 16, y: 39 },
                textOffset: isMobile ? { x: 0, y: 0 } : { x: 0, y: 0 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow>Schützengräben</SatAnnotationYellow>
                ),
                position: isMobile ? { x: 140, y: 29 } : { x: 95, y: 79 },
                textOffset: isMobile ? { x: 0, y: 0 } : { x: -60, y: 0 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow>Schützengräben</SatAnnotationYellow>
                ),
                position: isMobile ? { x: 140, y: 29 } : { x: 85, y: 96.5 },
                textOffset: isMobile ? { x: 0, y: 0 } : { x: 0, y: -60 },
              },
              {
                ...MapAnnotation,
                content: (
                  <>
                    <SatAnnotationYellow>
                      {LabelsDE.dragonsteeth}
                    </SatAnnotationYellow>{' '}
                    <image
                      x={-50}
                      y={5}
                      href={require('../../assets/icons/icons_dragonsteeth.png')}
                      height='100'
                      width='100'
                    />
                  </>
                ),
                position: isMobile ? { x: 22.5, y: 37 } : { x: 11.5, y: 26 },
                textOffset: isMobile ? { x: 80, y: 40 } : { x: 0, y: 80 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow>
                    {LabelsDE.dragonsteeth}
                  </SatAnnotationYellow>
                ),
                position: isMobile ? { x: 39, y: 91 } : { x: 27, y: 90 },
                textOffset: isMobile ? { x: 80, y: 0 } : { x: 80, y: 0 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow textAnchor={isMobile ? 'end' : 'start'}>
                    <tspan>Manche Schützengräben und</tspan>
                    <tspan x={0} dy={22}>
                      Panzersperren erstrecken sich
                    </tspan>
                    <tspan x={0} dy={22}>
                      über viele Kilometer
                    </tspan>
                  </SatAnnotationYellow>
                ),
                position: isMobile ? { x: 61, y: 86 } : { x: 32, y: 83 },
                textOffset: isMobile ? { x: 0, y: 0 } : { x: 120, y: 0 },
              },
              {
                lineStyle: {
                  stroke: 'white',
                },

                content: (
                  <>
                    <SatAnnotationWhite y={-40}>
                      <tspan>Schnellstrasse T0504 </tspan>
                      <tspan x={0} dy={18}>
                        kurz vor Popasna Richtung Luhansk
                      </tspan>
                    </SatAnnotationWhite>

                    <image
                      x={-50}
                      y={-90}
                      href={require('../../assets/icons/icons_arrow.png')}
                      width={100}
                    />
                  </>
                ),
                position: isMobile
                  ? {
                      x: 150,
                      y: 0,
                    }
                  : { x: width < 1200 ? 186 : 50, y: 27.5 },
              },
              {
                lineStyle: {
                  stroke: 'white',
                },

                content: (
                  <>
                    <image
                      x={60}
                      y={35}
                      href={require('../../assets/icons/icons_north.png')}
                      width={15}
                    />

                    <image
                      x={width < 950 ? -120 : -120}
                      y={20}
                      href={require('../../assets/icons/icons_ua.png')}
                      width={width < 950 ? 180 : 150}
                    />
                  </>
                ),
                position: isMobile
                  ? {
                      x: 50,
                      y: width < 500 ? 93 : 23.5,
                    }
                  : { x: 86, y: width > 1200 ? 24 : 26 },
              },
              {
                ...MapAnnotation,
                content: (
                  <SatAnnotationYellow>
                    Einschläge von
                    <tspan x={0} dy={22}>
                      Artilleriegeschossen
                    </tspan>
                  </SatAnnotationYellow>
                ),
                position: isMobile ? { x: 36, y: 81 } : { x: 20, y: 78 },
                textOffset: { x: 40, y: 0 },
              },

              {
                ...MapAnnotation,
                content: (
                  <>
                    <SatAnnotationYellow textAnchor='start'>
                      <tspan x={isMobile ? -30 : 0}>
                        Zum Teil gibt es bis fünf
                      </tspan>
                      <tspan x={isMobile ? -30 : 0} dy={22}>
                        Verteidigungslinien, die so angelegt sind,
                      </tspan>
                      <tspan x={isMobile ? -30 : 0} dy={22}>
                        dass sie Angreifer auf die Strassen drängen
                      </tspan>
                      <tspan x={isMobile ? -30 : 0} dy={22}>
                        sollen. So sind sie dann ein leichtes
                      </tspan>
                      <tspan x={isMobile ? -30 : 0} dy={22}>
                        Ziel für die Artillerie.
                      </tspan>
                    </SatAnnotationYellow>{' '}
                  </>
                ),
                position: isMobile ? { x: 18, y: 69 } : { x: 10, y: 62 },
              },
            ]}
          />
          <Header
            style={{ marginTop: width < 950 && width > 712 ? '160px' : '0' }}
          >
            <Title>
              <small>Vor der Frühjahrsoffensive:</small>So graben sich die
              Russen ein
            </Title>
            <Lead>
              Eine einmalige Auswertung von Satellitenbildern zeigt, wie sich
              das russische Militär auf den Angriff der Ukrainer vorbereitet
              hat. Die grosse Frage: Halten die Stellungen?
            </Lead>
            <Authors colorMode={colorMode} className='ContentMetaInfo'>
              <span key={72202328}>
                <a
                  href={`https://www.${tenant.key}.ch/author/16970675/dominik-balmer`}
                >
                  Dominik Balmer
                </a>
              </span>
              <span key={84880321}>
                <a
                  href={`https://www.${tenant.key}.ch/author/84880321/sebastian-broschinski`}
                >
                  Sebastian Broschinski
                </a>
              </span>
            </Authors>
          </Header>
        </Center>
      )}
      {debug && debugText}
      {(part === '2' || debug) && (
        <>
          <TextWidth>
            <ChartTitle>
              Auf Satellitenbildern erkennbare Verteidigungsanlagen
            </ChartTitle>
            <ChartKey>
              <KeyRow>
                <KeySwatch>
                  <img src={require('../../assets/icons/icons_point.png')} />
                </KeySwatch>
                <KeyLabel>Abschnitt einer Befestigungsanlage</KeyLabel>
              </KeyRow>
              <KeyRow>
                <KeySwatch>
                  <img src={require('../../assets/icons/icons_hatch.png')} />
                </KeySwatch>
                <KeyLabel>Von Russland kontrolliertes Gebiet</KeyLabel>
              </KeyRow>
            </ChartKey>
          </TextWidth>
          <Center style={{ marginTop: '30px' }}>
            <AnnotatedImage
              maxWidth={'1365px'}
              img={require('../../assets/maps/ukraine_full_' +
                (isMobile ? 'mob' : 'desk') +(isDarkMode ? '_dm' : '') +
                '.jpg')}
              source='Quelle: Brady Africk'
              annotations={[
                {
                  content: (
                    <LabelUnimportant fill='var(--gray-5)'>0</LabelUnimportant>
                  ),
                  position: isMobile ? { x: 74, y: 8 } : { x: 0.4, y: 97.5 },
                },
                {
                  content: <LabelUnimportant>100</LabelUnimportant>,
                  position: isMobile ? { x: 144, y: 50 } : { x: 9.6, y: 97.5 },
                },
                {
                  content: <LabelUnimportant>200 km</LabelUnimportant>,
                  position: isMobile ? { x: 94, y: 8 } : { x: 17.8, y: 97.5 },
                },
                {
                  content: (
                    <LabelUnimportant>{LabelsDE.dnipro}</LabelUnimportant>
                  ),
                  position: isMobile ? { x: 44, y: 50 } : { x: 57, y: 50 },
                },
                {
                  content: (
                    <LabelUnimportant>{LabelsDE.odessa}</LabelUnimportant>
                  ),
                  position: isMobile ? { x: 419, y: 63 } : { x: 21, y: 73 },
                },
                {
                  content: (
                    <LabelUnimportant>{LabelsDE.mykolaiv}</LabelUnimportant>
                  ),
                  position: isMobile ? { x: 149, y: 63 } : { x: 35, y: 68 },
                },
                {
                  content: (
                    <LabelUnimportant>{LabelsDE.saporischa}</LabelUnimportant>
                  ),
                  position: isMobile ? { x: 149, y: 63 } : { x: 54, y: 58 },
                },
                {
                  content: (
                    <LabelUnimportant>{LabelsDE.mariupol}</LabelUnimportant>
                  ),
                  position: isMobile ? { x: 69, y: 64 } : { x: 79, y: 69 },
                  textOffset: isMobile ? { x: 0, y: 30 } : { x: 0, y: 0 },
                },
                {
                  content: (
                    <LabelUnimportant>{LabelsDE.charkiw}</LabelUnimportant>
                  ),
                  position: isMobile ? { x: 49, y: 33 } : { x: 68, y: 32 },
                },
                {
                  content: <LabelUnimportant>{LabelsDE.sumy}</LabelUnimportant>,
                  position: isMobile ? { x: 36, y: 23 } : { x: 54, y: 21 },
                },
                {
                  content: <SmallLabel>{LabelsDE.tokmak}</SmallLabel>,
                  position: isMobile ? { x: 49, y: 63 } : { x: 62.5, y: 65 },
                  textOffset: isMobile ? { x: 0, y: -30 } : { x: 0, y: -50 },
                  showMarker: isMobile ? false : true,
                },
                {
                  content: <SmallLabel>{LabelsDE.cherson}</SmallLabel>,
                  position: isMobile ? { x: 17, y: 69 } : { x: 36, y: 72 },
                },
                {
                  content: (
                    <SmallLabel y={isMobile ? -15 : 0}>
                      {LabelsDE.donetsk}
                    </SmallLabel>
                  ),
                  position: isMobile ? { x: 72, y: 54 } : { x: 77, y: 55 },
                  textOffset: isMobile ? { x: -30, y: -30 } : { x: 0, y: 0 },
                },
                {
                  content: <SmallLabel>{LabelsDE.bachmut}</SmallLabel>,
                  position: isMobile ? { x: 77, y: 46 } : { x: 80, y: 47 },
                  textOffset: isMobile ? { x: -40, y: -40 } : { x: 0, y: 0 },
                },
                {
                  content: (
                    <Region stroke='rgba(255,255,255,.4)' strokeWidth={6}>
                      {LabelsDE.crimea}
                    </Region>
                  ),
                  position: isMobile ? { x: 35, y: 85 } : { x: 50, y: 88 },
                },
                {
                  content: <Region>{LabelsDE.crimea}</Region>,
                  position: isMobile ? { x: 35, y: 85 } : { x: 50, y: 88 },
                },
                {
                  content: <Region>{LabelsDE.blacksea}</Region>,
                  position: isMobile ? { x: 52, y: 97 } : { x: 28, y: 88 },
                },
                {
                  content: <Region>{LabelsDE.asozsea}</Region>,
                  position: isMobile ? { x: 162, y: 78 } : { x: 68, y: 80 },
                },
                {
                  content: <Capital>{LabelsDE.kiew}</Capital>,
                  position: isMobile ? { x: 150, y: 0 } : { x: 23, y: 27 },
                },
                {
                  content: (
                    <Country fill={colors.bb3}>{LabelsDE.ukraine}</Country>
                  ),
                  position: isMobile ? { x: 18, y: 30 } : { x: 18, y: 40 },
                },
                {
                  content: (
                    <Country fill={colors.bb2}>{LabelsDE.russia}</Country>
                  ),
                  position: isMobile ? { x: 70, y: 20 } : { x: 80, y: 16 },
                },
              ]}
            />
          </Center>
        </>
      )}
      {debug && debugText}
      {(part === '3' || debug) && (
        <>
          {/* <TextWidth>
            <ChartKey style={{ marginTop: '30px' }}>
              <KeyRow>
                <KeySwatch>
                  <img src={require('../../assets/icons/icons_point.png')} />
                </KeySwatch>
                <KeyLabel>Abschnitt einer Befestigungsanlage</KeyLabel>
              </KeyRow>
              <KeyRow>
                <KeySwatch>
                  <img src={require('../../assets/icons/icons_hatch.png')} />
                </KeySwatch>
                <KeyLabel>Von Russland kontrolliertes Gebiet</KeyLabel>
              </KeyRow>
            </ChartKey>
          </TextWidth> */}
          <ContentWidth>
            <Grid style={{ marginTop: '0px' }}>
              <div className='quarter'>
                <ChartTitle>Vor Oktober 2022</ChartTitle>
                <ChartTitleWrapper>
                  <ChartSub>
                    Auf den Satellitenbildern sind erst wenige
                    Befestigungsanlagen zu sehen
                  </ChartSub>
                </ChartTitleWrapper>
                <AnnotatedImage
                  img={require('../../assets/maps/Desktop_Ukr_before_okt22'+(isDarkMode ? '_dm' : '')+'.jpg')}
                  annotations={SmallMapAnnotations}
                />
              </div>
              <div className='quarter'>
                <ChartTitle>Oktober und November 2022</ChartTitle>
                <ChartTitleWrapper>
                  <ChartSub>
                    Vor allem im Süden rund um Cherson bauen die Russen erste
                    Befestigungsanlagen
                  </ChartSub>
                </ChartTitleWrapper>
                <AnnotatedImage
                  img={require('../../assets/maps/Desktop_Ukr_okt_nov_22'+(isDarkMode ? '_dm' : '')+'.jpg')}
                  annotations={SmallMapAnnotations}
                />
              </div>
              <div className='quarter'>
                <ChartTitle>Dezember '22 bis Januar '23</ChartTitle>
                <ChartTitleWrapper>
                  <ChartSub>
                    Das russische Militär baut Befestigungsanlagen im Osten der
                    Ukraine und auf der russischen Seite der Grenze
                  </ChartSub>
                </ChartTitleWrapper>
                <AnnotatedImage
                  img={require('../../assets/maps/Desktop_Ukr_dez22_jan23'+(isDarkMode ? '_dm' : '')+'.jpg')}
                  annotations={SmallMapAnnotations}
                />
              </div>
              <div className='quarter'>
                <ChartTitle>Ab Februar 2023</ChartTitle>
                <ChartTitleWrapper>
                  <ChartSub>
                    Die Frontlinie zu den besetzten Regionen Donezk und Luhansk
                    wird befestigt. Auch die Anlagen auf der Krim werden weiter
                    ausgebaut.
                  </ChartSub>
                </ChartTitleWrapper>
                <AnnotatedImage
                  img={require('../../assets/maps/Desktop_Ukr_after_feb23'+(isDarkMode ? '_dm' : '')+'.jpg')}
                  annotations={SmallMapAnnotations}
                />
              </div>
            </Grid>
          </ContentWidth>
        </>
      )}
      {debug && debugText}
      {(part === '4' || debug) && (
        <>
          <ContentWidth>
            <Grid>
              <div className='half'>
                <AnnotatedImage img={require('../../assets/maps/stepne.jpg')} />
                <Bu>
                  Schützengräben in der Nähe der Stadt Stepne westlich von
                  Cherson sollen das Vorrücken von ukrainischen Truppen
                  verhindern.
                </Bu>
              </div>
              <div className='half'>
                <AnnotatedImage
                  img={require('../../assets/maps/velykablahovischenka.jpg')}
                />
                <Bu>
                  Auch die Paralellstrasse nördlich von Stepne bei Welyka
                  Blahowischtschenka ist befestigt.
                </Bu>
                <Source>Quelle: Maxar</Source>
              </div>
            </Grid>
          </ContentWidth>
        </>
      )}
      {debug && debugText}
      {(part === '5' || debug) && (
        <Center>
          <AnnotatedImage
            maxWidth={'1365px'}
            img={require('../../assets/maps/sat_crimea_' +
              (isMobile ? 'mob' : 'desk') +(isDarkMode ? '_dm' : '')+
              '.jpg')}
            source='Quelle: Brady Africk'
            annotations={[
              {
                content: (
                  <LabelUnimportantInverted>
                    {LabelsDE.mykolaiv}
                  </LabelUnimportantInverted>
                ),
                position: isMobile ? { x: 140, y: 70 } : { x: 11.5, y: 26 },
              },
              {
                content: <RegionInverted>{LabelsDE.crimea}</RegionInverted>,
                position: isMobile ? { x: 40, y: 70 } : { x: 40, y: 68 },
              },
              {
                content: <Country fill={colors.bb2}>{LabelsDE.russia}</Country>,
                position: isMobile ? { x: 190, y: 80 } : { x: 90, y: 80 },
              },
              {
                content: <Region>{LabelsDE.blacksea}</Region>,
                position: isMobile ? { x: 70, y: 94 } : { x: 14, y: 84 },
              },
              {
                content: <Region>{LabelsDE.asozsea}</Region>,
                position: isMobile ? { x: 170, y: 94 } : { x: 70, y: 54 },
              },
              {
                content: isMobile ? (
                  <SatAnnotationGray>{LabelsDE.mariupol}</SatAnnotationGray>
                ) : (
                  <SatAnnotationWhite>{LabelsDE.mariupol}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 170, y: 50 } : { x: 90, y: 21 },
                textOffset: isMobile ? { x: 0, y: 30 } : { x: 0, y: 0 },
              },
              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.melitopol}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 170, y: 94 } : { x: 57, y: 33 },
              },
              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.cherson}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 10.5, y: 37 } : { x: 18.5, y: 33 },
                textOffset: { x: 0, y: 0 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.tokmak}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 75.5, y: 22.5 } : { x: 62.5, y: 17 },
                textOffset: isMobile ? { x: 0, y: -50 } : { x: 0, y: 70 },
                lineStyle: { stroke: 'white' },
              },
            ]}
          />
        </Center>
      )}
      {(part === '6' || debug) && (
        <Center>
          <AnnotatedImage
            maxWidth={'1365px'}
            img={require('../../assets/maps/sat_tokmak_' +
              (isMobile ? 'mob' : 'desk') +(isDarkMode ? '_dm' : '')+
              '.jpg')}
            source='Quelle: Brady Africk, © OpenStreetMap contributors'
            annotations={[
              {
                content: (
                  <SatAnnotationWhite>
                    {LabelsDE.saporischschja}
                  </SatAnnotationWhite>
                ),
                position: isMobile ? { x: 37, y: 9 } : { x: 39, y: 9 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.tokmak}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 45, y: 46 } : { x: 53, y: 49 },
                textOffset: isMobile ? { x: 0, y: 30 } : { x: 60, y: 0 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite y={-18}>
                    {LabelsDE.bilmak}
                  </SatAnnotationWhite>
                ),
                position: isMobile ? { x: 90, y: 40 } : { x: 88, y: 40 },
                textOffset: isMobile ? { x: -30, y: 30 } : { x: -60, y: 0 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite>
                    {LabelsDE.berdschansk}
                  </SatAnnotationWhite>
                ),
                position: isMobile ? { x: 93, y: 73 } : { x: 90, y: 82 },
                textOffset: isMobile ? { x: -30, y: -15 } : { x: -60, y: 0 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.melitopol}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 26, y: 70 } : { x: 40, y: 78 },
                textOffset: isMobile ? { x: 0, y: 30 } : { x: 60, y: 0 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite>
                    {LabelsDE.mychajliwka}
                  </SatAnnotationWhite>
                ),
                position: isMobile ? { x: 140, y: 70 } : { x: 33, y: 47 },
                textOffset: isMobile ? { x: 0, y: 30 } : { x: 0, y: 60 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationYellow>
                    {LabelsDE.akwsaporischschja}
                  </SatAnnotationYellow>
                ),
                position: isMobile ? { x: 140, y: 70 } : { x: 9, y: 28 },
                textOffset: isMobile ? { x: 0, y: 30 } : { x: 60, y: 0 },
                lineStyle: { stroke: 'var(--yellow-main)' },
              },
              {
                content: <Region>{LabelsDE.asozsea}</Region>,
                position: isMobile ? { x: 55, y: 98 } : { x: 168, y: 80 },
              },
              {
                lineStyle: {
                  stroke: 'white',
                },

                content: (
                  <>
                    <image
                      href={require('../../assets/icons/icons_overview_tokmak.png')}
                      width={isMobile ? 120 : 200}
                    />
                  </>
                ),
                position: isMobile
                  ? {
                      x: 68,
                      y: 80,
                    }
                  : { x: 2, y: 2 },
              },
            ]}
          />
        </Center>
      )}
      {(part === '7' || debug) && (
        <Center>
          <AnnotatedImage
            maxWidth={'1365px'}
            img={require('../../assets/maps/sat_crimea_' +
              (isMobile ? 'mob' : 'desk') +(isDarkMode ? '_dm' : '')+
              '.jpg')}
            source='Quelle: Brady Africk, © OpenStreetMap contributors'
            annotations={[
              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.cherson}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 10.5, y: 19 } : { x: 13, y: 22 },
                textOffset: isMobile ? { x: 0, y: -30 } : { x: 0, y: 0 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: <RegionInverted>{LabelsDE.crimea}</RegionInverted>,
                position: isMobile ? { x: 55, y: 80 } : { x: 60, y: 94 },
              },

              {
                content: <Region>{LabelsDE.blacksea}</Region>,
                position: isMobile ? { x: 20, y: 57 } : { x: 14, y: 84 },
              },

              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.oleschki}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 130, y: 24 } : { x: 20, y: 29 },
                textOffset: isMobile ? { x: 0, y: 40 } : { x: 0, y: 70 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite>
                    {LabelsDE.novakachovka}
                  </SatAnnotationWhite>
                ),
                position: isMobile ? { x: 45, y: 12 } : { x: 46, y: 17 },
                textOffset: isMobile ? { x: 0, y: 60 } : { x: -20, y: -70 },
                lineStyle: { stroke: 'white' },
              },

              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.dschankoj}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 79, y: 67 } : { x: 75, y: 85 },
                textOffset: isMobile ? { x: 0, y: 30 } : { x: 0, y: -70 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite>{LabelsDE.armjansk}</SatAnnotationWhite>
                ),
                position: isMobile ? { x: 52, y: 46 } : { x: 52, y: 62 },
                textOffset: isMobile ? { x: 40, y: 0 } : { x: 60, y: 0 },
                lineStyle: { stroke: 'white' },
              },
              {
                content: (
                  <SatAnnotationWhite>
                    {LabelsDE.krasnoperekopsk}
                  </SatAnnotationWhite>
                ),
                position: isMobile ? { x: 55, y: 55 } : { x: 55, y: 72 },
                textOffset: isMobile ? { x: 0, y: 50 } : { x: 70, y: 0 },
                lineStyle: { stroke: 'white' },
              },
              {
                lineStyle: {
                  stroke: 'white',
                },

                content: (
                  <>
                    <image
                      href={require('../../assets/icons/icons_overview_crimea.png')}
                      width={isMobile ? 120 : 200}
                    />
                  </>
                ),
                position: isMobile
                  ? {
                      x: 68,
                      y: 80,
                    }
                  : { x: 2, y: 2 },
              },
            ]}
          />
        </Center>
      )}{' '}
      {debug && debugText}
      {(part === '8' || debug) && (
        <>
          <ContentWidth>
            <Grid>
              <div className='half'>
                <AnnotatedImage img={require('../../assets/maps/Tomak.jpg')} />
                <Bu>
                  Die ukrainische Stadt Tokmak ist vollständig von
                  Schützengräben umgeben.
                </Bu>
              </div>
              <div className='half'>
                <AnnotatedImage img={require('../../assets/maps/crimea.jpg')} />
                <Bu>
                  Auch einige Küstenabschnitte auf der Krim sind mit
                  Befestigungsanlagen ausgerüstet worden.
                </Bu>
                <Source>Quelle: Maxar, Planet Labs</Source>
              </div>
            </Grid>
          </ContentWidth>
        </>
      )}
      {debug && debugText}
    </AppContainer>
  )
}
const ChartKey = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const KeyRow = styled.div`
  flex: 0 0 100%;
  display: flex;
  line-height: 1.4em;
`

const KeySwatch = styled.div`
  flex: 0 0 18px;
  img {
    max-width: 100%;
  }
`
const KeyLabel = styled.div`
  font-size: 15px;
  padding-left: 8px;
`

const Authors = styled.div<{ colorMode: 'light' | 'dark' | undefined }>`
  color: var(--text-color);
  font-size: 1.2rem;
  font-family: var(--font-sans-serif);
  line-height: 1.8rem;
  /* border-top: 1px solid rgba(255, 255, 255, 0.2); */

  padding-top: 2rem;
  a {
    color: white;
    text-decoration: none;
    border-bottom: 1px solid white;
    &:hover {
      opacity: 0.7;
    }
  }
  span {
    /* white-space: nowrap; */
  }
  span::after {
    color: white;

    content: ', ';
  }
  span:last-child::after {
    content: '';
  }
`
const Source = styled.div`
  font-size: 12px;
  color: var(--gray-3);
`
const Bu = styled.div`
  font-size: 15px;
  line-height: 21px;
`

const ChartTitleWrapper = styled.div`
  min-height: 4.5em;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 5px;
  text-align: left;
  @media screen and (max-width: 1024px) {
    min-height: 3.5em;
  }
  @media screen and (max-width: 900px) {
    min-height: 3.5em;
  }
`
const ChartTitle = styled.div`
  flex: 0 0 100%;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5em;
  margin-bottom: 10px;
  margin-top: 60px;
`
const ChartSub = styled.div`
  flex: 0 0 100%;
  font-size: 15px;
  font-weight: 300;
  line-height: 1.4em;
  color: var(--gray-3);
  @media screen and (max-width: 900px) {
    margin-bottom: 10px;
  }
`
const Header = styled.header`
  padding-top: 30px;
  color: white;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  max-width: 668px;
  @media screen and (max-width: 900px) {
    max-width: calc(100vw - 40px);
  }
  @media screen and (max-width: 711px) {
    max-width: calc(100vw - 40px);
    width: calc(100vw - 40px);
    padding-top: 30px;
  }
`
const Title = styled.h1`
  font-family: var(--font-serif);
  font-size: 60px;
  line-height: 1.1em;
  letter-spacing: 1px;
  margin-bottom: 30px;

  @media screen and (max-width: 900px) {
    font-size: 38px;
    line-height: 1.2em;
  }
  small {
    display: inline-block;
    width: 100%;
    font-size: 30px;
    line-height: 1.4em;
    @media screen and (max-width: 900px) {
      font-size: 20px;
      margin-bottom: 10px;
    }
  }
`
const Lead = styled.div`
  font-size: 20px;
  margin-bottom: 15px;
  @media screen and (max-width: 900px) {
    font-size: 18px;
    line-height: 1.4em;
  }
`
const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 60px;
  margin-bottom: 60px;
  gap: 15px;
  .half {
    width: calc(50% - 20px);
    flex: 0 0 calc(50% - 20px);
    padding: 0px;
    @media screen and (max-width: 1024px) {
      flex: 0 0 calc(48%);
    }
    @media screen and (max-width: 712px) {
      padding-left: 20px;
      flex: 0 0 calc(100vw - 50px);
    }
  }
  .quarter {
    flex: 0 0 calc(23.5%);
    padding: 0px;
    @media screen and (max-width: 1024px) {
      flex: 0 0 calc(48%);
    }
    @media screen and (max-width: 712px) {
      padding-left: 20px;
      flex: 0 0 calc(100vw - 50px);
    }
  }
`
const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 60px;
`
const AppContainer = styled(TamediaStyles)`${GlobalVariables}
  margin-top: -16px;
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  max-width: 100vw;
  overflow-x: hidden;
  p {
    font-family: var(--font-serif);
    font-size: 18px;
  }
`
export default MainComponent
